import * as React from 'react';
import { DisplayDate } from 'owa-datetime';
import {
    AgendaEventViewBase,
    Detail,
    AgendaEventViewBaseProps,
    ButtonDetailPart,
} from 'owa-agenda-event-views';
import { TimeUntilView } from './AgendaEventTimeUntilView';
import { getDurationString } from '../selectors/userConfigSelectors';
import { formatUserTime } from '../selectors/dateTimeSelectors';
import { PersonaDetailView } from './PersonaDetailView';
import { observer } from 'mobx-react';
import { getProviderDetails } from '../utils/getProviderDetails';
import { differenceInMinutes } from 'owa-datetime';
import { getCalendarEventIcons } from 'owa-calendar-event-icons';
import { observableNow } from 'owa-observable-datetime';

const styles = require('./AgendaEventViewUpNext.scss');
const classNames = require('classnames/bind').bind(styles);

export interface UpNextProps {
    startTime: DisplayDate;
    endTime: DisplayDate;
    organizerEmail: string;
    organizerName: string;
    location: string;
    subject: string;
    isCancelled: boolean;
    isMeeting: boolean;
    className: string;
    provider: string;
    joinUrl: string;
    hasAttachments: boolean;
    isRecurring: boolean;
    isPrivate: boolean;
    isRecurringException: boolean;
    charmId: number;
}

export type AgendaEventViewUpNextProps = Omit<
    AgendaEventViewBaseProps,
    'details' | 'timeDetails' | 'icons'
> &
    UpNextProps;

function withAgendaEventViewUpNextProps(
    AgendaEventViewBase: React.ComponentClass<AgendaEventViewBaseProps>
): React.ComponentType<AgendaEventViewUpNextProps> {
    const AgendaViewWithUpNext = observer(
        class extends React.Component<AgendaEventViewBaseProps & UpNextProps> {
            render() {
                const {
                    startTime,
                    endTime,
                    isCancelled,
                    subject,
                    location,
                    organizerEmail,
                    organizerName,
                    isMeeting,
                    className,
                    joinUrl,
                    provider,
                    hasAttachments,
                    isRecurring,
                    isPrivate,
                    isRecurringException,
                    charmId,
                    ...passThroughProps
                } = this.props;
                const details: Detail[] = [];

                const colorScheme = passThroughProps.colorScheme;
                details.push({
                    getTextProps: () => ({
                        text: subject,
                        className: classNames(styles.subject, { isCancelled: isCancelled }),
                    }),
                    getCharmControllProps: charmId
                        ? () => ({ iconId: charmId, className: styles.charm })
                        : null,
                    colorScheme: colorScheme,
                });
                if (location) {
                    details.push({
                        getTextProps: () => ({ text: location }),
                    });
                }
                if (isMeeting && organizerEmail) {
                    details.push({
                        renderCustomDetailPart: () => (
                            <PersonaDetailView
                                organizerEmail={organizerEmail}
                                organizerName={organizerName}
                            />
                        ),
                    });
                }
                if (joinUrl && differenceInMinutes(startTime, observableNow()) < 15) {
                    const { icon, providerString } = getProviderDetails(provider);
                    const buttonDetail: ButtonDetailPart = {
                        buttonProps: {
                            href: joinUrl,
                            iconProps: {
                                iconName: icon,
                                styles: { root: styles.providerIcon },
                            },
                            target: '_blank',
                            text: providerString,
                            onClick: this.onButtonClick,
                        },
                        colorScheme: colorScheme,
                    };
                    details.push(buttonDetail);
                }

                return (
                    <div className={styles.outerContainer}>
                        <TimeUntilView
                            startDate={startTime}
                            endDate={endTime}
                            containerClassName={styles.timeContainer}
                        />
                        <AgendaEventViewBase
                            {...passThroughProps}
                            timeDetails={[
                                {
                                    getTextProps: () => ({
                                        text: formatUserTime(startTime),
                                        className: styles.timePrimaryText,
                                    }),
                                },
                                {
                                    getTextProps: () => ({
                                        text: getDurationString(startTime, endTime),
                                        className: styles.timeSecondaryText,
                                    }),
                                },
                            ]}
                            icons={getCalendarEventIcons(
                                hasAttachments,
                                isRecurring,
                                isPrivate,
                                isRecurringException
                            )}
                            details={details}
                            className={classNames(styles.container, className)}
                        />
                    </div>
                );
            }

            private onButtonClick(evt: React.MouseEvent<EventTarget>): void {
                evt.stopPropagation();
            }
        }
    );

    return AgendaViewWithUpNext;
}

export const AgendaEventViewUpNext = withAgendaEventViewUpNextProps(AgendaEventViewBase);
